import React, { useMemo, useState } from 'react';
import {
  criterionOptionsFor,
  tagChipLabel,
  tagSourceLabel,
} from '../../constants/evidenceTagging';
import { formatAnswerItems } from '../../utils/otherFactorsSurfacing';
import { RespondentBadge } from './triageDecisionShared';
import EvidenceTagForm from './EvidenceTagForm';

/**
 * ReferralEvidencePanel
 * ---------------------
 * The source-evidence slide-over for the triage decision wizards: every
 * submitted referral/triage answer, readable from any wizard step, so the
 * clinician never has to conclude from memory.
 *
 * When `onAddTag` is provided each answer row carries a "Tag" affordance with
 * the same choices as the history review's Tag button (TDI-394): a criterion,
 * a risk, an other factor or something to add to the conclusion, with an
 * optional note; where it came from follows from the respondent. Tags are
 * clinician-applied only — the panel never suggests or pre-selects anything —
 * and show on the decision screen that matches their kind.
 *
 * Finding an answer never requires scrolling the full referral: the panel
 * carries a text search across questions and answers, a respondent filter,
 * and collapsible per-form sections. Searching auto-expands the matches.
 *
 * Props:
 *   - open, onClose
 *   - pathway: 'adhd' | 'asd' — which criteria the tags may point at
 *   - schemas: { [formKey]: schema } — loaded form schemas
 *   - formInstances: { [`${formKey}_${role}`]: { answers, ... } }
 *   - tags: [{ id, criterionKey, source, label, value }] — current tags
 *   - onAddTag?: (tag) => void — omit to hide the tagging affordance
 *   - onRemoveTag?: (tagId) => void
 */

const ROLE_LABELS = { guardian: 'Parent/carer', school_user: 'School' };

const RESPONDENT_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'guardian', label: ROLE_LABELS.guardian },
  { id: 'school_user', label: ROLE_LABELS.school_user },
];

/** Flatten a schema into [{ questionId, question }] including group children. */
function schemaQuestions(schema) {
  const out = [];
  for (const section of schema?.sections || []) {
    for (const q of section.questions || []) {
      if (q.questionId) out.push(q);
      for (const child of q.children || []) {
        if (child.questionId) out.push(child);
      }
    }
  }
  return out;
}

export default function ReferralEvidencePanel({
  open,
  onClose,
  pathway,
  schemas = {},
  formInstances = {},
  tags = [],
  onAddTag,
  onRemoveTag,
}) {
  // The answered rows, grouped per form instance (form x respondent), in the
  // schema's own question order.
  const instanceGroups = useMemo(() => {
    const groups = [];
    Object.entries(schemas).forEach(([formKey, schema]) => {
      if (!schema) return;
      const questions = schemaQuestions(schema);
      Object.entries(ROLE_LABELS).forEach(([role, roleLabel]) => {
        const instance = formInstances[`${formKey}_${role}`];
        const answers = instance?.answers;
        if (!answers) return;
        const rows = [];
        questions.forEach((question) => {
          const values = formatAnswerItems(question, answers[question.questionId]);
          if (!values.length) return;
          rows.push({
            questionId: question.questionId,
            label: question.label || question.questionId,
            values,
          });
        });
        if (!rows.length) return;
        groups.push({
          key: `${formKey}_${role}`,
          formKey,
          title: schema.title || formKey,
          role,
          respondent: roleLabel,
          rows,
        });
      });
    });
    return groups;
  }, [schemas, formInstances]);

  const [query, setQuery] = useState('');
  const [respondentFilter, setRespondentFilter] = useState('all');
  // Collapsed form sections, keyed by group key. Only honoured while not
  // searching — a search always shows its matches.
  const [collapsedKeys, setCollapsedKeys] = useState(() => new Set());

  const normalisedQuery = query.trim().toLowerCase();
  const filteredGroups = useMemo(() => (
    instanceGroups
      .filter((group) => respondentFilter === 'all' || group.role === respondentFilter)
      .map((group) => {
        if (!normalisedQuery) return group;
        const rows = group.rows.filter(
          (row) => row.label.toLowerCase().includes(normalisedQuery)
            || row.values.some((value) => value.toLowerCase().includes(normalisedQuery)),
        );
        return { ...group, rows };
      })
      .filter((group) => group.rows.length > 0)
  ), [instanceGroups, respondentFilter, normalisedQuery]);

  const toggleCollapsed = (key) =>
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label="Referral evidence">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} aria-hidden="true" />
      <div className="absolute inset-y-0 right-0 flex w-full max-w-xl flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div className="text-left">
            <h3 className="text-base font-semibold text-gray-900">Referral evidence</h3>
            <p className="mt-0.5 text-xs text-gray-500">
              {onAddTag
                ? 'Every submitted answer, with the option to tag an answer.'
                : 'Every submitted answer from the referral and triage forms.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close referral evidence"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
            </svg>
          </button>
        </div>

        {instanceGroups.length > 0 && (
          <div className="space-y-2.5 border-b border-gray-200 px-5 py-3">
            <label className="block text-xs font-medium text-gray-600">
              Search answers
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="For example sensory, sleep, friendships"
                className="mt-1 block w-full rounded-md border-2 border-gray-200 px-2 py-1.5 text-sm font-normal text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </label>
            <div className="flex items-center gap-1.5" role="group" aria-label="Filter by respondent">
              {RESPONDENT_FILTERS.map((filter) => {
                const active = respondentFilter === filter.id;
                return (
                  <button
                    key={filter.id}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setRespondentFilter(filter.id)}
                    className={`rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${
                      active
                        ? 'bg-primary/10 text-primary ring-primary/30'
                        : 'bg-white text-gray-600 ring-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {filter.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-4 text-left">
          {instanceGroups.length === 0 ? (
            <p className="text-sm text-gray-500">
              No submitted form answers are available for this referral.
            </p>
          ) : filteredGroups.length === 0 ? (
            <div className="text-left">
              <p className="text-sm text-gray-500">
                No answers match your search.
              </p>
              <button
                type="button"
                onClick={() => { setQuery(''); setRespondentFilter('all'); }}
                className="mt-2 text-xs font-medium text-primary underline hover:text-primary/80"
              >
                Clear search and filters
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredGroups.map((group) => {
                const collapsed = !normalisedQuery && collapsedKeys.has(group.key);
                const sectionId = `evidence-group-${group.key}`;
                return (
                  <section key={group.key} aria-label={`${group.title} — ${group.respondent}`}>
                    <div className="flex items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={() => toggleCollapsed(group.key)}
                        aria-expanded={!collapsed}
                        aria-controls={sectionId}
                        className="flex min-w-0 items-center gap-1.5 text-left"
                      >
                        <svg
                          className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${collapsed ? '-rotate-90' : ''}`}
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.06l3.71-3.83a.75.75 0 1 1 1.08 1.04l-4.25 4.39a.75.75 0 0 1-1.08 0L5.21 8.27a.75.75 0 0 1 .02-1.06Z" clipRule="evenodd" />
                        </svg>
                        <h4 className="truncate text-sm font-semibold text-gray-900">{group.title}</h4>
                        <span className="flex-shrink-0 text-xs font-normal text-gray-500">
                          {group.rows.length} {group.rows.length === 1 ? 'answer' : 'answers'}
                        </span>
                      </button>
                      <RespondentBadge respondent={group.respondent} />
                    </div>
                    {!collapsed && (
                      <div id={sectionId} className="mt-2 space-y-3">
                        {group.rows.map((row) => (
                          <EvidenceAnswerRow
                            key={row.questionId}
                            row={row}
                            group={group}
                            pathway={pathway}
                            tags={tags.filter(
                              (t) => t.formKey === group.formKey
                                && t.role === group.role
                                && t.questionId === row.questionId,
                            )}
                            onAddTag={onAddTag}
                            onRemoveTag={onRemoveTag}
                          />
                        ))}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Truncate a line for display in tag chips. */
const shortBullet = (text, max = 60) =>
  text.length > max ? `${text.slice(0, max - 1)}…` : text;

function EvidenceAnswerRow({ row, group, pathway, tags, onAddTag, onRemoveTag }) {
  const [tagFormOpen, setTagFormOpen] = useState(false);
  const canTag = Boolean(onAddTag) && criterionOptionsFor(pathway).length > 0;

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/70 p-3">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs font-medium text-gray-600">{row.label}</p>
        {canTag && (
          <button
            type="button"
            onClick={() => setTagFormOpen((v) => !v)}
            className="whitespace-nowrap text-xs font-medium text-primary hover:text-primary/80"
          >
            {tagFormOpen ? 'Cancel tag' : 'Tag evidence'}
          </button>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {row.values.map((value, i) => (
          <span
            key={i}
            className="inline-flex items-center rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-800"
          >
            {value}
          </span>
        ))}
      </div>

      {tags.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-1.5" aria-label={`Tags — ${row.label}`}>
          {tags.map((tag) => (
            <li
              key={tag.id}
              className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary ring-1 ring-inset ring-primary/20"
            >
              {tagChipLabel(pathway, tag)}
              {tagSourceLabel(tag.source) && (
                <span className="font-normal text-primary/80">· {tagSourceLabel(tag.source)}</span>
              )}
              {typeof tag.partIndex === 'number' && (
                <span className="font-normal text-primary/80">
                  · “{shortBullet(tag.value || '', 32)}”
                </span>
              )}
              {onRemoveTag && (
                <button
                  type="button"
                  onClick={() => onRemoveTag(tag.id)}
                  className="text-primary/70 hover:text-primary"
                  aria-label={`Remove ${tagChipLabel(pathway, tag)} tag — ${row.label}`}
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {tagFormOpen && canTag && (
        <div className="mt-3 rounded-md border border-gray-200 bg-white p-3">
          {/* The same choices as the history review's Tag button (TDI-394). */}
          <EvidenceTagForm
            pathway={pathway}
            targets={[{
              formKey: group.formKey,
              role: group.role,
              questionId: row.questionId,
              label: row.label,
              values: row.values,
            }]}
            onSubmit={onAddTag}
            onClose={() => setTagFormOpen(false)}
          />
        </div>
      )}
    </div>
  );
}
